import React, { useEffect, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { ClickAwayListener } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import ClearIcon from '@material-ui/icons/Clear';
import BlockIcon from '@material-ui/icons/Block';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import styles from './checklistComponent.module.scss';

let activeStatusDropdownId = null;
let closeActiveStatusDropdown = null;

const InspectionStatusPill = ({
  status,
  onChange,
  enableNA,
  disabled,
  nomenclature,
  isTolerance,
  defaultSatisfactory,
  onToggle,
  statusOptions,
  allowSelection,
  forceSelectStatusLabel,
  emptyLabel,
  textStyle,
  intl
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownId = useRef(Symbol('inspection-status-pill'));
  const hasMounted = useRef(false);
  const pillRef = useRef(null);
  const listRef = useRef(null);

  const onToggleRef = useRef(onToggle);
  useEffect(() => {
    onToggleRef.current = onToggle;
  });

  const resolvedStatusOptions = statusOptions || [
    true,
    false,
    ...(enableNA ? ['N/A'] : [])
  ];
  const canOpenDropdown = !disabled
    && (typeof allowSelection === 'boolean' ? allowSelection : resolvedStatusOptions.length > 0);

  const clearActiveDropdown = () => {
    if (activeStatusDropdownId === dropdownId.current) {
      activeStatusDropdownId = null;
      closeActiveStatusDropdown = null;
    }
  };

  useEffect(() => () => {
    if (activeStatusDropdownId === dropdownId.current) {
      activeStatusDropdownId = null;
      closeActiveStatusDropdown = null;
    }
  }, []);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    onToggleRef.current(isOpen);
  }, [isOpen]);

  const DROPDOWN_OFFSET = 4;

  const placeList = useCallback(() => {
    if (!pillRef.current || !listRef.current) return;
    const pill = pillRef.current.getBoundingClientRect();
    const list = listRef.current;
    list.style.position = 'fixed';
    list.style.zIndex = '9999';
    list.style.minWidth = '160px';
    list.style.left = `${pill.left}px`;
    list.style.width = `${pill.width}px`;
    list.style.top = `${pill.bottom + DROPDOWN_OFFSET}px`;
    list.style.bottom = 'auto';
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    let rafId;
    const rafPlaceList = () => { rafId = requestAnimationFrame(placeList); };
    window.addEventListener('scroll', rafPlaceList, true);
    window.addEventListener('resize', rafPlaceList);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', rafPlaceList, true);
      window.removeEventListener('resize', rafPlaceList);
    };
  }, [isOpen, placeList]);

  const sat = nomenclature?.sat_nom || intl.formatMessage({ id: 'inspection.status.satisfactory', defaultMessage: 'Satisfactory' });
  const unsat = nomenclature?.unsat_nom || intl.formatMessage({ id: 'inspection.status.unsatisfactory', defaultMessage: 'Unsatisfactory' });
  const na = nomenclature?.na_nom || intl.formatMessage({ id: 'inspection.status.not_applicable', defaultMessage: 'Not Applicable' });
  const selectStatusLabel = intl.formatMessage({ id: 'inspection.status.select_status', defaultMessage: 'Select Status' });

  const getStatusConfig = (currentStatus) => {
    switch (currentStatus) {
      case true:
      case 'Satisfactory':
        return {
          label: sat,
          className: styles.successPill,
          icon: <CheckIcon style={{ fill: '#fff', width: '13px', height: '13px' }} />,
          value: true
        };
      case false:
      case 'Unsatisfactory':
        return {
          label: unsat,
          className: styles.failurePill,
          icon: <ClearIcon style={{ fill: '#fff', width: '13px', height: '13px' }} />,
          value: false
        };
      case 'N/A':
      case 'Not Applicable':
        return {
          label: na,
          className: styles.naPill,
          icon: <BlockIcon style={{ fill: '#fff', width: '13px', height: '13px' }} />,
          value: 'N/A'
        };
      default:
        if (forceSelectStatusLabel) {
          return {
            label: emptyLabel || selectStatusLabel,
            className: styles.untouchedPill,
            icon: null,
            value: undefined,
            isPlaceholder: true
          };
        }
        if (isTolerance && !defaultSatisfactory) {
          return {
            label: '',
            className: styles.untouchedPill,
            icon: null,
            value: undefined,
            isPlaceholder: true
          };
        }
        return {
          label: defaultSatisfactory ? sat : selectStatusLabel,
          className: defaultSatisfactory ? styles.successPill : styles.untouchedPill,
          icon: defaultSatisfactory ? <CheckIcon style={{ fill: '#fff', width: '13px', height: '13px' }} /> : null,
          value: undefined,
          isPlaceholder: !defaultSatisfactory
        };
    }
  };

  const currentConfig = getStatusConfig(status);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (canOpenDropdown) {
      if (isOpen) {
        setIsOpen(false);
        clearActiveDropdown();
        return;
      }
      if (closeActiveStatusDropdown && activeStatusDropdownId !== dropdownId.current) {
        closeActiveStatusDropdown();
      }
      activeStatusDropdownId = dropdownId.current;
      closeActiveStatusDropdown = () => {
        setIsOpen(false);
        clearActiveDropdown();
      };
      setIsOpen(true);
    }
  };

  const handleSelect = (val) => {
    onChange(val);
    setIsOpen(false);
    clearActiveDropdown();
  };

  const handleKeyDown = (e, callback) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      callback(e);
    }
  };

  return (
    <div className={styles.statusPillWrapper}>
      <ClickAwayListener onClickAway={() => {
        setIsOpen(false);
        clearActiveDropdown();
      }}>
        <div className={styles.dropdownContainer}>
          <div
            ref={pillRef}
            role="button"
            tabIndex={0}
            className={`${currentConfig.className} ${disabled || !canOpenDropdown ? styles.disabled : ''} ${isOpen ? styles.open : ''}`}
            onClick={handleToggle}
            onKeyDown={e => handleKeyDown(e, handleToggle)}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
          >
            {currentConfig.icon && (
              <span className={styles.iconCircle}>
                {currentConfig.icon}
              </span>
            )}
            <span className={styles.pillText}>
              <span style={textStyle || { margin: '0px' }}>{currentConfig.label}</span>
            </span>
            {canOpenDropdown && <ExpandMoreIcon className={styles.chevron} />}
          </div>


          {isOpen && (
            <ul
              ref={(el) => {
                listRef.current = el;
                if (listRef.current) {
                  placeList();
                }
              }}
              className={styles.statusDropdownList}
              role="listbox"
            >
              {resolvedStatusOptions.includes(true) && (
                <li
                  role="option"
                  aria-selected={status === true}
                  tabIndex={0}
                  onClick={() => handleSelect(true)}
                  onKeyDown={e => handleKeyDown(e, () => handleSelect(true))}
                >
                  <span className={`${styles.iconCircle} ${styles.bgSuccess}`}>
                    <CheckIcon style={{ fill: '#fff', width: '13px', height: '13px' }} />
                  </span>
                  <span>{sat}</span>
                </li>
              )}
              {resolvedStatusOptions.includes(false) && (
                <li
                  role="option"
                  aria-selected={status === false}
                  tabIndex={0}
                  onClick={() => handleSelect(false)}
                  onKeyDown={e => handleKeyDown(e, () => handleSelect(false))}
                >
                  <span className={`${styles.iconCircle} ${styles.bgFailure}`}>
                    <ClearIcon style={{ fill: '#fff', width: '13px', height: '13px' }} />
                  </span>
                  <span>{unsat}</span>
                </li>
              )}
              {resolvedStatusOptions.includes('N/A') && (
                <li
                  role="option"
                  aria-selected={status === 'N/A'}
                  tabIndex={0}
                  onClick={() => handleSelect('N/A')}
                  onKeyDown={e => handleKeyDown(e, () => handleSelect('N/A'))}
                >
                  <span className={`${styles.iconCircle} ${styles.bgNa}`}>
                    <BlockIcon style={{ fill: '#fff', width: '13px', height: '13px' }} />
                  </span>
                  <span>{na}</span>
                </li>
              )}
            </ul>
          )}
        </div>
      </ClickAwayListener>
    </div>
  );
};

InspectionStatusPill.propTypes = {
  status: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  enableNA: PropTypes.bool,
  disabled: PropTypes.bool,
  nomenclature: PropTypes.shape({
    sat_nom: PropTypes.string,
    unsat_nom: PropTypes.string,
    na_nom: PropTypes.string
  }),
  isTolerance: PropTypes.bool,
  defaultSatisfactory: PropTypes.bool,
  onToggle: PropTypes.func,
  statusOptions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.bool, PropTypes.string])),
  allowSelection: PropTypes.bool,
  forceSelectStatusLabel: PropTypes.bool,
  emptyLabel: PropTypes.string,
  textStyle: PropTypes.shape({}),
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};

InspectionStatusPill.defaultProps = {
  status: undefined,
  enableNA: false,
  disabled: false,
  nomenclature: {},
  isTolerance: false,
  defaultSatisfactory: false,
  onToggle: () => { },
  statusOptions: undefined,
  allowSelection: undefined,
  forceSelectStatusLabel: false,
  emptyLabel: undefined,
  textStyle: undefined
};

export default injectIntl(InspectionStatusPill);
